import {
  summarizeShots,
  summarizeGoals,
  summarizePasses,
  summarizeSimpleList,
  summarizeSaves,
  summarizeDuels,
  summarizeRebounds,
  summarizeClearances,
  summarizeClearanceRecoveries,
  summarizeGoalkeeperTicks,
  extractAttemptedPasses,
  extractCompletedPasses,
  extractPlayerXg,
  resolvePlayerName,
  getCount,
  toFiniteNumber,
  buildAdvancedPlayerStats
} from './gameStatsHelpers.js';
import { canonicalPlayerKey, inferTeamsFromMatch, normalizeTeamLabel } from './teamUtils.js';
import { isFflSevenAsideMatch } from './fflUtils.js';
import { buildFflFeatureRow } from './fflFeatureExtractor.js';

const POSITION_HEATMAP_COLS = 12;
const POSITION_HEATMAP_ROWS = 8;
const DEFAULT_FIELD_WIDTH = 840;
const DEFAULT_FIELD_HEIGHT = 420;

function cloneHeatmapGrid(grid) {
  if (!Array.isArray(grid)) return null;
  return grid.map((row) => {
    if (!Array.isArray(row)) return Array(POSITION_HEATMAP_COLS).fill(0);
    return row.map((value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    });
  });
}

function sanitizePositionAnalyticsEntry(entry, fallbackName, fieldWidth, fieldHeight) {
  if (!entry || typeof entry !== 'object') return null;
  const samples = Number(entry.samples) || 0;
  const sumX = Number(entry.sumX) || 0;
  const sumY = Number(entry.sumY) || 0;
  const samplesByTeam = entry.samplesByTeam && typeof entry.samplesByTeam === 'object'
    ? {
        red: Number(entry.samplesByTeam.red) || 0,
        blue: Number(entry.samplesByTeam.blue) || 0,
        spec: Number(entry.samplesByTeam.spec) || 0
      }
    : { red: 0, blue: 0, spec: 0 };
  return {
    name: entry.name || fallbackName || null,
    samples,
    sumX,
    sumY,
    minX: Number.isFinite(entry.minX) ? entry.minX : null,
    maxX: Number.isFinite(entry.maxX) ? entry.maxX : null,
    minY: Number.isFinite(entry.minY) ? entry.minY : null,
    maxY: Number.isFinite(entry.maxY) ? entry.maxY : null,
    heatmap: cloneHeatmapGrid(entry.heatmap),
    samplesByTeam,
    lastTeam: entry.lastTeam || null,
    fieldWidth: Number.isFinite(fieldWidth) && fieldWidth > 0 ? fieldWidth : DEFAULT_FIELD_WIDTH,
    fieldHeight: Number.isFinite(fieldHeight) && fieldHeight > 0 ? fieldHeight : DEFAULT_FIELD_HEIGHT
  };
}

function buildPositionAnalyticsLookup(match, teamLookup = new Map()) {
  const map = new Map();
  if (!match) return map;
  const source = match.positionAnalytics;
  const width = Number(match?.stadium?.bg?.width) || Number(match?.stadium?.width) || DEFAULT_FIELD_WIDTH;
  const height = Number(match?.stadium?.bg?.height) || Number(match?.stadium?.height) || DEFAULT_FIELD_HEIGHT;
  if (Array.isArray(source)) {
    source.forEach((entry) => {
      const key = canonicalPlayerKey(entry?.name);
      if (!key) return;
      const sanitized = sanitizePositionAnalyticsEntry(entry, entry?.name, width, height);
      if (sanitized) map.set(key, sanitized);
    });
  }
  else if (source && typeof source === 'object') {
    Object.keys(source).forEach((key) => {
      const entry = source[key];
      const lowerKey = canonicalPlayerKey(key) || canonicalPlayerKey(entry?.name);
      if (!lowerKey) return;
      const sanitized = sanitizePositionAnalyticsEntry(entry, entry?.name || key, width, height);
      if (sanitized) map.set(lowerKey, sanitized);
    });
  }
  const fallback = buildFallbackPositionAnalytics(match, teamLookup);
  if (map.size === 0) {
    fallback.forEach((value, key) => map.set(key, value));
  } else {
    fallback.forEach((value, key) => {
      if (!map.has(key)) map.set(key, value);
    });
  }
  return map;
}

function buildFallbackPositionAnalytics(match, teamLookup = new Map()) {
  const map = new Map();
  if (!match) return map;
  const stadiumWidth = Number(match?.stadium?.bg?.width ?? match?.stadium?.width) || 0;
  const stadiumHeight = Number(match?.stadium?.bg?.height ?? match?.stadium?.height) || 0;
  const width = stadiumWidth > 0 ? stadiumWidth : DEFAULT_FIELD_WIDTH;
  const height = stadiumHeight > 0 ? stadiumHeight : DEFAULT_FIELD_HEIGHT;
  const halfWidth = width / 2 || 1;
  const halfHeight = height / 2 || 1;
  const rows = POSITION_HEATMAP_ROWS;
  const cols = POSITION_HEATMAP_COLS;
  const events = Array.isArray(match?.touches) && match.touches.length
    ? match.touches
    : (Array.isArray(match?.kicks) ? match.kicks : []);
  if (!events.length) return map;

  const ensureEntry = (rawName) => {
    const name = resolvePlayerName(rawName);
    const key = canonicalPlayerKey(name);
    if (!key) return { entry: null, key: null };
    if (!map.has(key)) {
      const baseHeatmap = Array.from({ length: rows }, () => Array(cols).fill(0));
      map.set(key, {
        name,
        samples: 0,
        sumX: 0,
        sumY: 0,
        minX: null,
        maxX: null,
        minY: null,
        maxY: null,
        heatmap: baseHeatmap,
        samplesByTeam: { red: 0, blue: 0, spec: 0 },
        lastTeam: teamLookup.get(key) || null,
        fieldWidth: width,
        fieldHeight: height
      });
    }
    return { entry: map.get(key), key };
  };

  events.forEach((event) => {
    if (!event) return;
    const { entry, key } = ensureEntry(event.player || event.nick || event.name);
    if (!entry || !key) return;
    const x = Number(event.x);
    const y = Number(event.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    entry.samples += 1;
    entry.sumX += x;
    entry.sumY += y;
    entry.minX = entry.minX === null ? x : Math.min(entry.minX, x);
    entry.maxX = entry.maxX === null ? x : Math.max(entry.maxX, x);
    entry.minY = entry.minY === null ? y : Math.min(entry.minY, y);
    entry.maxY = entry.maxY === null ? y : Math.max(entry.maxY, y);
    const eventTeam = normalizeTeamLabel(event.team || event.teamKey || event.playerTeam) || teamLookup.get(key) || null;
    if (eventTeam === 'red') entry.samplesByTeam.red += 1;
    else if (eventTeam === 'blue') entry.samplesByTeam.blue += 1;
    else entry.samplesByTeam.spec += 1;
    if (eventTeam) entry.lastTeam = eventTeam;
    const normalizedX = Math.max(0, Math.min(0.9999, (x + halfWidth) / (width || (halfWidth * 2))));
    const normalizedY = Math.max(0, Math.min(0.9999, (y + halfHeight) / (height || (halfHeight * 2))));
    const col = Math.max(0, Math.min(cols - 1, Math.floor(normalizedX * cols)));
    const row = Math.max(0, Math.min(rows - 1, Math.floor(normalizedY * rows)));
    entry.heatmap[row][col] += 1;
  });

  Array.from(map.entries()).forEach(([key, entry]) => {
    if (!entry.samples) {
      map.delete(key);
    } else {
      if (entry.minX === null) entry.minX = 0;
      if (entry.maxX === null) entry.maxX = 0;
      if (entry.minY === null) entry.minY = 0;
      if (entry.maxY === null) entry.maxY = 0;
    }
  });

  return map;
}

export function clamp(value, min, max) {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export function safeNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export function roundTo(value, decimals = 4) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  const factor = Math.pow(10, decimals);
  return Math.round(num * factor) / factor;
}

export const DERIVED_METRIC_KEYS = [
  'xg',
  'goals',
  'assists',
  'shotsOnTarget',
  'passesPerMinute',
  'passAccuracy',
  'touchesPerMinute',
  'savesPerMinute',
  'defensiveImpact',
  'clearancePerMinute',
  'recoveryPerMinute',
  'depthImpactScore',
  'pressureRelief',
  'pressureRate',
  'progressiveBalance',
  'forwardShare',
  'duelWinRate',
  'keeperShare',
  'possessionImpact',
  'scoringTouchShare',
  'turnoverRate',
  'progressivePassesPerMinute',
  'finalThirdEntriesPerMinute',
  'forcedTurnoversPerMinute',
  'counterRunsPerMinute',
  'xgPreventedPerShot',
  'sweeperActionsPerMinute'
];

export const ROLE_WEIGHTS = {
  keeper: { offense: 0.3, defense: 1.25, playmaking: 0.7, pressure: 1.2, duel: 0.8 },
  defender: { offense: 0.65, defense: 1.1, playmaking: 0.85, pressure: 1.05, duel: 1.1 },
  midfielder: { offense: 0.95, defense: 0.95, playmaking: 1.15, pressure: 1.0, duel: 0.95 },
  forward: { offense: 1.3, defense: 0.6, playmaking: 0.9, pressure: 0.75, duel: 0.85 }
};

function computeAdvancedScore(summary, derived, baseAnalysis, minutes, scale) {
  const metrics = summary.metrics || {};
  const role = summary.role || 'midfielder';
  const team = summary.team === 'red' ? 'red' : (summary.team === 'blue' ? 'blue' : null);
  const margin = team === 'red' ? baseAnalysis.margin : team === 'blue' ? -baseAnalysis.margin : 0;
  const dominance = baseAnalysis.teamDominance || { red: 0.5, blue: 0.5 };
  const teamDom = team === 'red' ? dominance.red : team === 'blue' ? dominance.blue : 0;
  const oppDom = team === 'red' ? dominance.blue : team === 'blue' ? dominance.red : 0;
  const dominanceEdge = clamp(teamDom - oppDom, -1, 1);
  const safeVal = (value, def = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : def;
  };

  const minutesSafe = Math.max(1, Number(minutes) || 1);
  const scaleSafe = Number.isFinite(scale) ? scale : (10 / minutesSafe);

  const goalsPer10 = safeVal(metrics.goals) * scaleSafe;
  const assistsPer10 = safeVal(metrics.assists) * scaleSafe;
  const shotsOnTarget = safeVal(metrics.shotsOnTarget) * 0.08 * scaleSafe;
  const xg = safeVal(metrics.xg);
  const secondaryChain = safeVal(metrics.secondaryAssistsChain);
  const tertiaryChain = safeVal(metrics.tertiaryAssistsChain);
  const goalChainTouches = safeVal(metrics.goalChainTouches);

  const progressivePasses = safeVal(metrics.progressivePasses);
  const progressiveDistance = safeVal(metrics.progressiveDistance);
  const finalThirdEntries = safeVal(metrics.finalThirdEntries);
  const keyPasses = safeVal(metrics.keyPasses);

  const possessionTouches = safeVal(metrics.possessionTouches);
  const scoringTouches = safeVal(metrics.scoringPossessionTouches);
  const turnoverTouches = safeVal(metrics.turnoverTouches);
  const pressuredTouches = safeVal(metrics.pressuredPossessionTouches);
  const pressureRelief = possessionTouches > 0 ? 1 - (pressuredTouches / possessionTouches) : 0;
  const scoringShare = possessionTouches > 0 ? scoringTouches / possessionTouches : 0;

  const forcedTurnovers = safeVal(metrics.forcedTurnovers);
  const recoveries = safeVal(metrics.recoveries);
  const finalThirdRecoveries = safeVal(metrics.finalThirdRecoveries);
  const counterRuns = safeVal(metrics.counterRuns);
  const turnoversCommitted = safeVal(metrics.turnoversCommitted);
  const dangerousTurnovers = safeVal(metrics.dangerousTurnovers);

  const xgPrevented = safeVal(metrics.xgPrevented);
  const sweeperActions = safeVal(metrics.sweeperActions);
  const saves = safeVal(metrics.saves);

  const duelDiff = safeVal(metrics.duelsWon) - safeVal(metrics.duelsLost);

  const offenseScore =
    goalsPer10 * 0.75 +
    assistsPer10 * 0.55 +
    xg * 0.45 +
    shotsOnTarget +
    goalChainTouches * 0.08 +
    secondaryChain * 0.12 +
    tertiaryChain * 0.07;

  const creationScore =
    (progressivePasses / minutesSafe) * 1.25 +
    (progressiveDistance / Math.max(1, minutesSafe * 35)) * 1.0 +
    (finalThirdEntries / minutesSafe) * 1.1 +
    (keyPasses / minutesSafe) * 1.35;

  const possessionScore =
    scoringShare * 2.0 +
    pressureRelief * 1.3 -
    (turnoverTouches / Math.max(1, minutesSafe)) * 0.5 -
    turnoversCommitted * 0.35;

  const defensiveScore =
    (forcedTurnovers / minutesSafe) * 1.45 +
    (recoveries / minutesSafe) * 1.0 +
    (finalThirdRecoveries / minutesSafe) * 1.35 +
    (counterRuns / minutesSafe) * 0.8 +
    duelDiff * 0.06;

  const keeperScore =
    role === 'keeper'
      ? (xgPrevented * 1.9) + (saves * 0.22) + (sweeperActions / Math.max(1, minutesSafe) * 1.2)
      : (xgPrevented * 0.75) + (sweeperActions / Math.max(1, minutesSafe) * 0.6);

  const negatives = dangerousTurnovers * 0.7 + Math.max(0, pressuredTouches - (possessionTouches * 0.6)) * 0.05;

  let total = 6.1 + margin * 0.18 + dominanceEdge * 0.32;
  total += offenseScore + creationScore + possessionScore + defensiveScore + keeperScore;
  if (role === 'forward') total += offenseScore * 0.12 - defensiveScore * 0.06;
  if (role === 'midfielder') total += possessionScore * 0.18 + creationScore * 0.08;
  if (role === 'defender') total += defensiveScore * 0.22 - offenseScore * 0.05;
  if (role === 'keeper') total += keeperScore * 0.3 - offenseScore * 0.05;
  total -= negatives;

  return clamp(total, 2, 9.6);
}

export function calculateStats(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return { mean: 0, std: 0.25, max: 0 };
  }
  const sanitized = values.map((value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  });
  const len = sanitized.length;
  const mean = sanitized.reduce((sum, v) => sum + v, 0) / len;
  const variance = sanitized.reduce((sum, v) => {
    const diff = v - mean;
    return sum + diff * diff;
  }, 0) / len;
  const std = Math.sqrt(Math.max(variance, 0));
  return {
    mean,
    std: std < 0.25 ? 0.25 : std,
    max: Math.max(...sanitized)
  };
}

export function zScore(value, stats) {
  if (!stats) return 0;
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  const denom = stats.std || 0.25;
  return (num - stats.mean) / denom;
}

export function boundZ(value, limit = 3) {
  return clamp(value, -limit, limit);
}

function prepareTeamDerivedMetricsInternal(playerSummaries, minutes, totalTicks) {
  const derivedByNick = new Map();
  const perTeam = { red: [], blue: [] };
  const safeMinutes = Math.max(1, Number(minutes) || 1);
  const safeTicks = Math.max(1, Number(totalTicks) || 1);

  playerSummaries.forEach((summary) => {
    if (!summary || (summary.team !== 'red' && summary.team !== 'blue')) return;
    const metrics = summary.metrics || {};
    const duelWins = Number(metrics.duelsWon) || 0;
    const duelLosses = Number(metrics.duelsLost) || 0;
    const duelTotal = Math.max(1, duelWins + duelLosses);
    const defensiveInterventions =
      (Number(metrics.interceptions) || 0) +
      (Number(metrics.clearances) || 0) +
      (Number(metrics.reboundRecoveries) || 0) +
      (Number(metrics.clearanceRecoveries) || 0) +
      duelWins;
    const clearanceVolume = Math.max(
      Number(metrics.clearances) || 0,
      Number(metrics.clearanceEvents) || 0,
      Number(metrics.clearancesTotal) || 0
    );
    const possessionTouches = Number(metrics.possessionTouches) || 0;
    const scoringTouches = Number(metrics.scoringPossessionTouches) || 0;
    const turnoversCommitted = Number(metrics.turnoversCommitted) || 0;
    const progressivePasses = Number(metrics.progressivePasses) || 0;
    const finalThirdEntries = Number(metrics.finalThirdEntries) || 0;
    const forcedTurnovers = Number(metrics.forcedTurnovers) || 0;
    const counterRuns = Number(metrics.counterRuns) || 0;
    const xgFaced = Number(metrics.xgFaced) || 0;
    const xgPrevented = Number(metrics.xgPrevented) || 0;
    const sweeperActions = Number(metrics.sweeperActions) || 0;
    const recoveryVolume = (Number(metrics.reboundRecoveries) || 0) + (Number(metrics.clearanceRecoveries) || 0);
    const totalDirectional =
      (Number(metrics.passesForward) || 0) +
      (Number(metrics.passesLateral) || 0) +
      (Number(metrics.passesBackward) || 0);
    const forwardShare = totalDirectional > 0 ? (Number(metrics.passesForward) || 0) / totalDirectional : 0;
    const backwardShare = totalDirectional > 0 ? (Number(metrics.passesBackward) || 0) / totalDirectional : 0;
    const passAccuracy = Number.isFinite(metrics.passAccuracyPct)
      ? metrics.passAccuracyPct / 100
      : (Number(metrics.passAccuracy) || 0);
    const kicksTotal = Number(metrics.kicksTotal) || 0;
    const pressuredKicks = Number(metrics.pressuredKicks) || 0;
    const pressureRate = kicksTotal > 0 ? pressuredKicks / kicksTotal : 0;
    const pressureRelief = kicksTotal > 0 ? (kicksTotal - pressuredKicks) / kicksTotal : 0;

    const derived = {
      xg: Number(metrics.xg) || 0,
      goals: Number(metrics.goals) || 0,
      assists: Number(metrics.assists) || 0,
      shotsOnTarget: Number(metrics.shotsOnTarget) || 0,
      passesPerMinute: Number(metrics.passesPerMinute) || 0,
      passAccuracy,
      touchesPerMinute: Number(metrics.touchesPerMinute) || 0,
      savesPerMinute: Number(metrics.savesPerMinute) || 0,
      defensiveImpact: defensiveInterventions / safeMinutes,
      clearancePerMinute: clearanceVolume / safeMinutes,
      recoveryPerMinute: recoveryVolume / safeMinutes,
      depthImpactScore: Number(metrics.depthImpactScore) || 0,
      pressureRelief,
      pressureRate,
      progressiveBalance: forwardShare - backwardShare,
      forwardShare,
      duelWinRate: duelWins / duelTotal,
      keeperShare: (Number(metrics.gkTicks) || 0) / safeTicks,
      possessionImpact: possessionTouches / safeMinutes,
      scoringTouchShare: possessionTouches > 0 ? scoringTouches / possessionTouches : 0,
      turnoverRate: possessionTouches > 0 ? turnoversCommitted / Math.max(1, possessionTouches) : 0,
      progressivePassesPerMinute: progressivePasses / safeMinutes,
      finalThirdEntriesPerMinute: finalThirdEntries / safeMinutes,
      forcedTurnoversPerMinute: forcedTurnovers / safeMinutes,
      counterRunsPerMinute: counterRuns / safeMinutes,
      xgPreventedPerShot: xgFaced > 0 ? xgPrevented / xgFaced : 0,
      sweeperActionsPerMinute: sweeperActions / safeMinutes
    };

    derivedByNick.set(summary.nick, derived);
    perTeam[summary.team].push({ nick: summary.nick, derived });
  });

  const statsByTeam = { red: {}, blue: {} };
  DERIVED_METRIC_KEYS.forEach((key) => {
    statsByTeam.red[key] = calculateStats(perTeam.red.map((item) => item.derived[key]));
    statsByTeam.blue[key] = calculateStats(perTeam.blue.map((item) => item.derived[key]));
  });

  return { derivedByNick, statsByTeam };
}

function determineRole(summary, derived) {
  const keeperShare = Number(derived.keeperShare) || 0;
  const saves = Number(summary.metrics.saves) || 0;
  const defensiveImpact = Number(derived.defensiveImpact) || 0;
  const forwardShare = Number(derived.forwardShare) || 0;
  const progressive = Number(derived.progressiveBalance) || 0;
  const passesPerMinute = Number(derived.passesPerMinute) || 0;
  const touchesPerMinute = Number(derived.touchesPerMinute) || 0;
  const xg = Number(summary.metrics.xg) || 0;
  const goals = Number(summary.metrics.goals) || 0;

  if (keeperShare >= 0.12 || (keeperShare >= 0.07 && saves >= 3)) {
    return 'keeper';
  }

  if (xg >= 0.45 || goals >= 2 || forwardShare >= 0.6 || (xg >= 0.25 && forwardShare >= 0.5)) {
    return 'forward';
  }

  if (defensiveImpact >= 0.8 && forwardShare < 0.5) {
    return 'defender';
  }

  if (passesPerMinute >= 2.4 || touchesPerMinute >= 5 || Math.abs(progressive) <= 0.15) {
    return 'midfielder';
  }

  if (defensiveImpact >= 0.5 && forwardShare < 0.45) {
    return 'defender';
  }

  if (forwardShare >= 0.5) {
    return 'forward';
  }

  return 'midfielder';
}

const POSITION_ROLE_MAP = {
  GK: 'keeper',
  DM: 'defender',
  AM: 'midfielder',
  CM: 'midfielder',
  ST: 'forward',
  LW: 'forward',
  RW: 'forward',
  CB: 'defender'
};

function resolveFieldSize(metrics) {
  const width = Number(metrics.positionFieldWidth);
  const height = Number(metrics.positionFieldHeight);
  return {
    width: Number.isFinite(width) && width > 0 ? width : DEFAULT_FIELD_WIDTH,
    height: Number.isFinite(height) && height > 0 ? height : DEFAULT_FIELD_HEIGHT
  };
}

function computePlayerSpatial(summary, derived) {
  const metrics = summary.metrics || {};
  const { width, height } = resolveFieldSize(metrics);
  const halfWidth = width / 2 || 1;
  const halfHeight = height / 2 || 1;
  const orientation = summary.team === 'blue' ? -1 : 1;
  const lateralOrientation = summary.team === 'blue' ? -1 : 1;
  const avgX = Number(metrics.avgPosX);
  const avgY = Number(metrics.avgPosY);
  let depth = Number.isFinite(avgX) ? clamp((avgX / halfWidth) * orientation, -2, 2) : null;
  let lateral = Number.isFinite(avgY)
    ? clamp((avgY / halfHeight) * lateralOrientation, -1.5, 1.5)
    : null;
  const derivedForwardShare = Number(derived?.forwardShare);
  if (depth === null && Number.isFinite(derivedForwardShare)) {
    depth = clamp((derivedForwardShare - 0.5) * 2, -1.5, 1.5);
  }
  if (depth === null) depth = 0;
  if (lateral === null) lateral = 0;

  const heatmap = Array.isArray(metrics.positionHeatmap) ? metrics.positionHeatmap : null;
  let wideShare = 0;
  let cornerShare = 0;
  let topShare = 0;
  let bottomShare = 0;
  let topCornerShare = 0;
  let bottomCornerShare = 0;
  let defShare = 0;
  let midShare = 0;
  let attShare = 0;
  let entropy = 0;
  let centrality = 0;
  let verticalBalance = 0;
  let attMinusDef = 0;
  let total = 0;

  if (heatmap && heatmap.length) {
    const rows = heatmap.length;
    const cols = Array.isArray(heatmap[0]) ? heatmap[0].length : 0;
    if (cols > 0) {
      let weightedAbsDepth = 0;
      let topCount = 0;
      let bottomCount = 0;
      let wideCount = 0;
      let cornerCount = 0;
      let topCornerCount = 0;
      let bottomCornerCount = 0;
      const cellCounts = [];

      for (let r = 0; r < rows; r += 1) {
        const row = Array.isArray(heatmap[r]) ? heatmap[r] : [];
        for (let c = 0; c < cols; c += 1) {
          const count = Number(row[c]) || 0;
          if (count <= 0) continue;
          total += count;
          cellCounts.push(count);
          const normalizedX = ((c + 0.5) / cols - 0.5) * width;
          const orientedX = normalizedX * orientation;
          const normalizedY = ((r + 0.5) / rows - 0.5) * height;
          weightedAbsDepth += Math.abs(orientedX) * count;

          if (orientedX <= -width / 6) defShare += count;
          else if (orientedX >= width / 6) attShare += count;
          else midShare += count;

          if (normalizedY < -height * 0.1) topCount += count;
          else if (normalizedY > height * 0.1) bottomCount += count;

          if (Math.abs(normalizedY) >= height * 0.25) wideCount += count;

          const inCorner = orientedX >= width * 0.3 && Math.abs(normalizedY) >= height * 0.35;
          if (inCorner) {
            cornerCount += count;
            if (normalizedY < 0) topCornerCount += count;
            else bottomCornerCount += count;
          }
        }
      }

      if (total > 0) {
        const invTotal = 1 / total;
        defShare *= invTotal;
        attShare *= invTotal;
        midShare *= invTotal;
        wideShare = wideCount * invTotal;
        cornerShare = cornerCount * invTotal;
        topShare = topCount * invTotal;
        bottomShare = bottomCount * invTotal;
        topCornerShare = topCornerCount * invTotal;
        bottomCornerShare = bottomCornerCount * invTotal;
        attMinusDef = clamp(attShare - defShare, -1, 1);
        const avgAbsDepthNorm = (weightedAbsDepth / total) / halfWidth;
        centrality = clamp(1 - Math.min(1, avgAbsDepthNorm), 0, 1);
        verticalBalance = clamp((topCount - bottomCount) * invTotal, -1, 1);
        if (cellCounts.length > 1) {
          let entropySum = 0;
          cellCounts.forEach((count) => {
            const p = count * invTotal;
            entropySum -= p * Math.log(p);
          });
          const maxEntropy = Math.log(cellCounts.length);
          entropy = maxEntropy > 0 ? clamp(entropySum / maxEntropy, 0, 1) : 0;
        }
      }
    }
  }

  const samples = Number(metrics.positionSamples) || 0;
  const touches = Number(metrics.touches) || 0;

  return {
    depth,
    lateral,
    samples,
    touches,
    width,
    height,
    wideShare,
    cornerShare,
    topShare,
    bottomShare,
    topCornerShare,
    bottomCornerShare,
    attShare,
    midShare,
    defShare,
    attMinusDef,
    entropy,
    centrality,
    verticalBalance
  };
}

export function pickGoalkeeper(players, derivedByNick = null) {
  if (!players.length) return null;
  let best = players[0];
  let bestTicks = Number(best.metrics?.gkTicks) || 0;
  players.forEach((player) => {
    const ticks = Number(player.metrics?.gkTicks) || 0;
    if (ticks > bestTicks) {
      best = player;
      bestTicks = ticks;
    }
  });
  if (bestTicks <= 0) {
    const keeperByRole = players.find((p) => p.role === 'keeper');
    if (keeperByRole) return keeperByRole;
    let fallback = null;
    let bestScore = -Infinity;
    players.forEach((player) => {
      const metrics = player?.metrics || {};
      const lookup = derivedByNick instanceof Map
        ? derivedByNick.get(player?.nick)
        : null;
      const keeperShare = Number(lookup?.keeperShare) || 0;
      const saves = Number(metrics.saves) || 0;
      const defShare = Number(metrics.positionDefShare);
      const depth = Number(metrics.positionDepth);
      const sweeperActions = Number(metrics.sweeperActions) || 0;
      const xgFaced = Number(metrics.xgFaced) || 0;
      const depthScore = Number.isFinite(depth) ? -depth : 0;
      const defScore = Number.isFinite(defShare) ? defShare : 0;
      const score =
        keeperShare * 8 +
        defScore * 3 +
        depthScore * 2 +
        saves +
        sweeperActions * 0.25 +
        xgFaced * 0.1;
      if (score > bestScore) {
        bestScore = score;
        fallback = player;
      }
    });
    if (fallback) return fallback;
  }
  return best;
}

function scoreStrikerCandidate(spatial) {
  if (!spatial || typeof spatial !== 'object') return -Infinity;
  const depth = Number(spatial.depth);
  if (!Number.isFinite(depth)) return -Infinity;
  const absLat = Math.abs(Number(spatial.lateral) || 0);
  const wideShare = Number(spatial.wideShare);
  const cornerShare = Number(spatial.cornerShare);
  const centrality = Number(spatial.centrality);
  const attShare = Number(spatial.attShare);
  const midShare = Number(spatial.midShare);
  const widthPenalty =
    absLat * 1.4 +
    (Number.isFinite(wideShare) ? Math.max(0, wideShare - 0.05) * 2.4 : 0) +
    (Number.isFinite(cornerShare) ? Math.max(0, cornerShare - 0.04) * 2 : 0);
  const centralBonus =
    (Number.isFinite(centrality) ? centrality * 0.8 : 0) +
    (Number.isFinite(attShare) ? attShare * 0.6 : 0) -
    (Number.isFinite(midShare) ? Math.max(0, midShare - 0.45) * 0.5 : 0);
  return depth * 1.8 + centralBonus - widthPenalty;
}

function chooseStrikerCandidate(entries) {
  if (!Array.isArray(entries) || !entries.length) return null;
  let choice = null;
  let bestScore = -Infinity;
  entries.forEach((entry) => {
    const score = scoreStrikerCandidate(entry?.spatial);
    if (score > bestScore && Number.isFinite(score)) {
      bestScore = score;
      choice = entry;
    }
  });
  if (!choice) {
    entries.forEach((entry) => {
      const depth = Number(entry?.spatial?.depth);
      if (!choice || depth > Number(choice?.spatial?.depth || -Infinity)) {
        choice = entry;
      }
    });
  }
  return choice || entries[0] || null;
}

function scoreCenterBackCandidate(spatial) {
  if (!spatial || typeof spatial !== 'object') return -Infinity;
  const depth = Number(spatial.depth);
  const defShare = Number(spatial.defShare);
  const midShare = Number(spatial.midShare);
  const attShare = Number(spatial.attShare);
  const wideShare = Number(spatial.wideShare);
  const cornerShare = Number(spatial.cornerShare);
  const centrality = Number(spatial.centrality);
  const verticalBalance = Number(spatial.verticalBalance);

  const depthComponent = Number.isFinite(depth) ? Math.min(0, depth) * -5 : 0;
  const defComponent = Number.isFinite(defShare) ? defShare * 3.5 : 0;
  const midComponent = Number.isFinite(midShare) ? midShare * 1.2 : 0;
  const attPenalty = Number.isFinite(attShare) ? attShare * 1.5 : 0;
  const widthPenalty = Number.isFinite(wideShare) ? Math.max(0, wideShare - 0.08) * 5 : 0;
  const cornerPenalty = Number.isFinite(cornerShare) ? Math.max(0, cornerShare - 0.05) * 3 : 0;
  const centralBonus = Number.isFinite(centrality) ? centrality * 2 : 0;
  const balancePenalty = Number.isFinite(verticalBalance) ? Math.abs(verticalBalance) * 1.2 : 0;

  return depthComponent + defComponent + midComponent + centralBonus - attPenalty - widthPenalty - cornerPenalty - balancePenalty;
}

function chooseCenterBacks(entries, count = 2) {
  if (!Array.isArray(entries) || !entries.length || count <= 0) return [];
  const base = entries.map((entry, index) => {
    const score = scoreCenterBackCandidate(entry?.spatial);
    const depth = Number(entry?.spatial?.depth);
    return {
      entry,
      score: Number.isFinite(score) ? score : -Infinity,
      depth: Number.isFinite(depth) ? depth : Infinity,
      index
    };
  });

  const primary = base.filter((item) => Number.isFinite(item.depth) && item.depth <= -0.18);
  const fallback = base.filter((item) => !primary.includes(item));
  const sortDefenders = (list) => {
    list.sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      if (a.depth !== b.depth) return a.depth - b.depth;
      return a.index - b.index;
    });
  };

  sortDefenders(primary);
  sortDefenders(fallback);

  const combined = [...primary, ...fallback];

  combined.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    if (a.depth !== b.depth) return a.depth - b.depth;
    return a.index - b.index;
  });

  return combined.slice(0, count).map((item) => item.entry);
}

function chooseCentralMidCandidate(entries) {
  if (!Array.isArray(entries) || !entries.length) return null;
  let choice = null;
  let bestScore = -Infinity;
  entries.forEach((entry) => {
    const spatial = entry?.spatial || {};
    const absLat = Math.abs(Number(spatial.lateral) || 0);
    const depth = Number(spatial.depth);
    const midShare = Number(spatial.midShare);
    const wideShare = Number(spatial.wideShare);
    const cornerShare = Number(spatial.cornerShare);
    const centrality = Number(spatial.centrality);
    const midComponent = Number.isFinite(midShare) ? midShare * 1.2 : 0;
    const balanceComponent = Number.isFinite(depth) ? 1 - Math.min(1, Math.abs(depth)) : 0;
    const centralComponent = Number.isFinite(centrality) ? centrality * 0.6 : 0;
    const widthPenalty = Number.isFinite(wideShare) ? Math.max(0, wideShare - 0.6) * 1.5 : 0;
    const cornerPenalty = Number.isFinite(cornerShare) ? Math.max(0, cornerShare - 0.25) * 1.2 : 0;
    const lateralPenalty = Math.max(0, absLat - 0.9) * 2;
    const score = midComponent + balanceComponent + centralComponent - widthPenalty - cornerPenalty - lateralPenalty;
    if (!choice || score > bestScore) {
      bestScore = score;
      choice = entry;
    }
  });
  return choice || entries[0];
}

export function buildFflFeatureRows(match, summaries, minutes) {
  if (!isFflSevenAsideMatch(match)) return [];
  if (!Array.isArray(summaries) || !summaries.length) return [];
  const replayId = match?.replayId ?? match?.id ?? null;
  const matchIndexValue = Number.isFinite(Number(match?.matchIndex)) ? Number(match.matchIndex) : 0;
  return summaries
    .filter((summary) => summary && (summary.team === 'red' || summary.team === 'blue'))
    .map((summary) => buildFflFeatureRow({
      replayId,
      matchIndex: matchIndexValue,
      match,
      summary,
      minutes
    }));
}

function resolveWingPosition(summary, spatial) {
  const lateral = Number(spatial?.lateral);
  if (Number.isFinite(lateral) && Math.abs(lateral) >= 0.01) {
    return lateral <= 0 ? 'LW' : 'RW';
  }
  const topShare = Number(spatial?.topShare);
  const bottomShare = Number(spatial?.bottomShare);
  if (Number.isFinite(topShare) && Number.isFinite(bottomShare)) {
    return topShare >= bottomShare ? 'LW' : 'RW';
  }
  const topCorner = Number(spatial?.topCornerShare);
  const bottomCorner = Number(spatial?.bottomCornerShare);
  if (Number.isFinite(topCorner) && Number.isFinite(bottomCorner)) {
    return topCorner >= bottomCorner ? 'LW' : 'RW';
  }
  return 'LW';
}

function assignThreePlayerShape(players, derivedByNick) {
  if (players.length < 2) {
    players.forEach((summary) => { summary.position = summary.position || 'DM'; });
    return;
  }
  const enriched = players.map((summary) => {
    const derived = derivedByNick.get(summary.nick) || {};
    return { summary, spatial: computePlayerSpatial(summary, derived) };
  });
  enriched.sort((a, b) => a.spatial.depth - b.spatial.depth);
  enriched[0].summary.position = 'DM';
  const deepest = enriched[enriched.length - 1];
  if (deepest) deepest.summary.position = deepest.summary.position || 'ST';
  for (let i = 1; i < enriched.length - 1; i += 1) {
    enriched[i].summary.position = enriched[i].summary.position || 'ST';
  }
}

function assignFourPlayerShape(players, derivedByNick) {
  if (players.length < 3) {
    assignThreePlayerShape(players, derivedByNick);
    return;
  }
  const enriched = players.map((summary) => {
    const derived = derivedByNick.get(summary.nick) || {};
    return { summary, spatial: computePlayerSpatial(summary, derived) };
  });
  enriched.sort((a, b) => a.spatial.depth - b.spatial.depth);
  enriched[0].summary.position = 'DM';
  enriched[1].summary.position = 'AM';
  const deeper = enriched.slice(2);
  if (deeper.length) {
    let stEntry = deeper[0];
    deeper.forEach((entry) => {
      if (entry.spatial.depth > stEntry.spatial.depth) stEntry = entry;
    });
    if (stEntry) stEntry.summary.position = stEntry.summary.position || 'ST';
    deeper
      .filter((entry) => entry !== stEntry)
      .forEach(({ summary, spatial }) => {
        if (!summary.position) {
          summary.position = resolveWingPosition(summary, spatial);
        }
      });
  }
}

function assignSevenPlayerShape(players, derivedByNick) {
  if (players.length < 6) {
    assignFourPlayerShape(players, derivedByNick);
    return;
  }
  const enriched = players.map((summary) => {
    const derived = derivedByNick.get(summary.nick) || {};
    return { summary, spatial: computePlayerSpatial(summary, derived) };
  });
  enriched.sort((a, b) => a.spatial.depth - b.spatial.depth);
  const cbEntries = chooseCenterBacks(enriched, Math.min(2, enriched.length));
  const cbs = cbEntries.length ? cbEntries : enriched.slice(0, 2);
  const cbSet = new Set(cbs.map((entry) => entry.summary));
  cbs.forEach(({ summary }) => { summary.position = 'CB'; });
  const remainder = enriched.filter((entry) => !cbSet.has(entry.summary));
  if (remainder.length === 0) return;
  const stCandidate = chooseStrikerCandidate(remainder);
  if (stCandidate) stCandidate.summary.position = stCandidate.summary.position || 'ST';
  const poolAfterStriker = remainder.filter((entry) => entry !== stCandidate);
  let cmCandidate = chooseCentralMidCandidate(poolAfterStriker);
  if (cmCandidate && cmCandidate.summary.position !== 'ST') {
    cmCandidate.summary.position = 'CM';
  } else {
    cmCandidate = null;
  }
  const wings = poolAfterStriker.filter((entry) => entry !== cmCandidate);
  const assignments = wings.map((entry) => {
    const resolved = resolveWingPosition(entry.summary, entry.spatial);
    return { entry, role: resolved };
  });

  const normalizeLateral = (assignment) => {
    const lateral = Number(assignment.entry.spatial?.lateral);
    if (!Number.isFinite(lateral)) return 0;
    return lateral;
  };

  let hasLW = assignments.some((a) => a.role === 'LW');
  let hasRW = assignments.some((a) => a.role === 'RW');

  const counts = assignments.reduce((acc, assignment) => {
    if (assignment.role === 'LW') acc.LW += 1;
    if (assignment.role === 'RW') acc.RW += 1;
    return acc;
  }, { LW: 0, RW: 0 });

  if (counts.LW > 1 && counts.RW === 0 && assignments.length >= 2) {
    assignments.sort((a, b) => normalizeLateral(b) - normalizeLateral(a));
    assignments[0].role = 'RW';
    hasRW = true;
    hasLW = true;
  } else if (counts.RW > 1 && counts.LW === 0 && assignments.length >= 2) {
    assignments.sort((a, b) => normalizeLateral(a) - normalizeLateral(b));
    assignments[0].role = 'LW';
    hasLW = true;
    hasRW = true;
  }

  if (!hasLW && assignments.length) {
    assignments.sort((a, b) => normalizeLateral(a) - normalizeLateral(b));
    assignments[0].role = 'LW';
    hasLW = true;
    if (assignments.length > 1) {
      assignments.slice(1).forEach((assignment) => {
        if (assignment.role === 'LW') assignment.role = 'RW';
      });
    }
  }

  if (!hasRW && assignments.length > 1) {
    assignments.sort((a, b) => normalizeLateral(a) - normalizeLateral(b));
    assignments[assignments.length - 1].role = 'RW';
    hasRW = true;
    if (assignments.length > 1) {
      assignments.slice(0, assignments.length - 1).forEach((assignment) => {
        if (assignment.role === 'RW') assignment.role = 'LW';
      });
    }
  }

  assignments.forEach(({ entry, role }) => {
    if (!entry.summary.position) {
      entry.summary.position = role;
    }
  });
}

function assignTeamPositions(teamPlayers, derivedByNick) {
  if (!teamPlayers.length) return;
  const keeper = pickGoalkeeper(teamPlayers, derivedByNick);
  if (keeper) keeper.position = 'GK';
  const fieldPlayers = teamPlayers.filter((player) => player !== keeper);
  const activeFieldPlayers = fieldPlayers.filter((summary) => {
    const metrics = summary.metrics || {};
    const samples = Number(metrics.positionSamples) || 0;
    const touches = Number(metrics.touches) || 0;
    const passes = Number(metrics.passesCompleted) || 0;
    const goals = Number(metrics.goals) || 0;
    return samples > 0 || touches > 0 || passes > 0 || goals > 0;
  });
  const working = activeFieldPlayers.length ? activeFieldPlayers : fieldPlayers;
  const size = working.length + (keeper ? 1 : 0);
  if (size >= 7) {
    assignSevenPlayerShape(working, derivedByNick);
  } else if (size >= 4) {
    assignFourPlayerShape(working, derivedByNick);
  } else {
    assignThreePlayerShape(working, derivedByNick);
  }
}

function applyPositionTags(playerSummaries, derivedByNick) {
  const byTeam = { red: [], blue: [] };
  playerSummaries.forEach((summary) => {
    if (summary.team === 'red' || summary.team === 'blue') {
      byTeam[summary.team].push(summary);
    }
  });
  assignTeamPositions(byTeam.red, derivedByNick);
  assignTeamPositions(byTeam.blue, derivedByNick);
  playerSummaries.forEach((summary) => {
    if (!summary.position) return;
    const general = POSITION_ROLE_MAP[summary.position] || summary.role;
    if (general) summary.role = general;
  });
}

function computeRoleBaselines(playerSummaries, derivedByNick) {
  const buckets = {
    keeper: [],
    defender: [],
    midfielder: [],
    forward: []
  };

  playerSummaries.forEach((summary) => {
    if (!summary || (summary.team !== 'red' && summary.team !== 'blue')) return;
    const role = summary.role || 'midfielder';
    const derived = derivedByNick.get(summary.nick) || {};
    buckets[role].push({ summary, derived });
  });

  const result = {};
  Object.entries(buckets).forEach(([role, entries]) => {
    if (!entries.length) {
      result[role] = { count: 0 };
      return;
    }
    const aggregate = entries.reduce((acc, { summary, derived }) => {
      acc.xg += Number(summary.metrics.xg) || 0;
      acc.goals += Number(summary.metrics.goals) || 0;
      acc.assists += Number(summary.metrics.assists) || 0;
      acc.saves += Number(summary.metrics.saves) || 0;
      acc.passesPerMinute += Number(derived.passesPerMinute) || 0;
      acc.touchesPerMinute += Number(derived.touchesPerMinute) || 0;
      acc.defensiveImpact += Number(derived.defensiveImpact) || 0;
      acc.pressureRelief += Number(derived.pressureRelief) || 0;
      acc.pressureRate += Number(derived.pressureRate) || 0;
      return acc;
    }, {
      xg: 0,
      goals: 0,
      assists: 0,
      saves: 0,
      passesPerMinute: 0,
      touchesPerMinute: 0,
      defensiveImpact: 0,
      pressureRelief: 0,
      pressureRate: 0
    });

    const count = entries.length;
    result[role] = {
      count,
      avgXg: aggregate.xg / count,
      avgGoals: aggregate.goals / count,
      avgAssists: aggregate.assists / count,
      avgSaves: aggregate.saves / count,
      avgPassesPerMinute: aggregate.passesPerMinute / count,
      avgTouchesPerMinute: aggregate.touchesPerMinute / count,
      avgDefensiveImpact: aggregate.defensiveImpact / count,
      avgPressureRelief: aggregate.pressureRelief / count,
      avgPressureRate: aggregate.pressureRate / count
    };
  });

  return result;
}

export function prepareTeamDerivedMetrics(playerSummaries, minutes, totalTicks) {
  return prepareTeamDerivedMetricsInternal(playerSummaries, minutes, totalTicks);
}

export function buildMatchAnalysis(match) {
  if (!match) return null;
  const shots = Array.isArray(match.shots) ? match.shots : [];
  const goals = Array.isArray(match.goals) ? match.goals : [];
  const savesArr = Array.isArray(match.saves) ? match.saves : [];
  const passesArr = Array.isArray(match.passes) ? match.passes : [];
  const duelsArr = Array.isArray(match.duels) ? match.duels : [];
  const reboundsArr = Array.isArray(match.rebounds) ? match.rebounds : [];
  const reboundRecoveriesArr = Array.isArray(match.reboundRecoveries) ? match.reboundRecoveries : [];
  const clearancesArr = Array.isArray(match.clearances) ? match.clearances : [];
  const clearanceRecoveriesArr = Array.isArray(match.clearanceRecoveries) ? match.clearanceRecoveries : [];
  const gkArr = Array.isArray(match.gkOccupancy) ? match.gkOccupancy : [];
  const kicksArr = Array.isArray(match.kicks) ? match.kicks : [];
  const players = Array.isArray(match.player) ? match.player : [];
  const legacyPlayers = Array.isArray(match.players) ? match.players : [];
  let redTeam = Array.isArray(match.redTeam) ? [...match.redTeam] : [];
  let blueTeam = Array.isArray(match.blueTeam) ? [...match.blueTeam] : [];
  let inferredAssignments = null;
  if (!redTeam.length || !blueTeam.length) {
    inferredAssignments = inferTeamsFromMatch(match);
    if (inferredAssignments) {
      if (!redTeam.length) redTeam = inferredAssignments.red;
      if (!blueTeam.length) blueTeam = inferredAssignments.blue;
    }
  }

  const normalizeTeamName = (value) => {
    if (value === undefined || value === null) return null;
    const str = typeof value === 'string' ? value : (value.nick || value.name || value.player || '');
    if (typeof str !== 'string') return null;
    const trimmed = str.trim();
    return trimmed ? trimmed : null;
  };
  const redLookup = new Set(redTeam.map(normalizeTeamName).filter(Boolean).map((n) => n.toLowerCase()));
  const blueLookup = new Set(blueTeam.map(normalizeTeamName).filter(Boolean).map((n) => n.toLowerCase()));
  const teamLookup = new Map();
  redTeam.forEach((name) => {
    const normalized = normalizeTeamName(name);
    if (!normalized) return;
    const key = canonicalPlayerKey(normalized);
    if (key) teamLookup.set(key, 'red');
  });
  blueTeam.forEach((name) => {
    const normalized = normalizeTeamName(name);
    if (!normalized) return;
    const key = canonicalPlayerKey(normalized);
    if (key) teamLookup.set(key, 'blue');
  });

  const minutes = Math.max(1, (safeNumber(match.gameTicks) / 60) / 60);
  const totalTicks = Math.max(1, safeNumber(match.gameTicks));

  const possRed = Math.max(0, safeNumber(match.possRed));
  const possBlue = Math.max(0, safeNumber(match.possBlue));
  const possTotal = possRed + possBlue;
  const possessionShare = {
    red: possTotal > 0 ? clamp(possRed / possTotal, 0, 1) : 0.5,
    blue: possTotal > 0 ? clamp(possBlue / possTotal, 0, 1) : 0.5
  };

  const thirdsRaw = Array.isArray(match.thirds) ? match.thirds : [0, 0, 0];
  const thirdTotals = thirdsRaw.map((v) => Math.max(0, safeNumber(v)));
  const thirdSum = thirdTotals.reduce((sum, val) => sum + val, 0);
  const territoryShare = {
    red: thirdSum > 0 ? clamp(thirdTotals[2] / thirdSum, 0, 1) : 0.5,
    blue: thirdSum > 0 ? clamp(thirdTotals[0] / thirdSum, 0, 1) : 0.5
  };

  const { byShooter: shotMap, savedBy: shotSavedBy } = summarizeShots(shots, {
    stadium: match?.stadium,
    redTeam,
    blueTeam
  });
  const { goals: goalMap, assists: assistMap } = summarizeGoals(goals);
  const passMap = summarizePasses(passesArr);
  const directionalPassMap = new Map();
  passesArr.forEach((pass) => {
    if (!pass || typeof pass !== 'object') return;
    const name = resolvePlayerName(pass.player ?? pass.passer ?? pass.author ?? pass.from ?? pass.source);
    if (!name) return;
    const dir = typeof pass.direction === 'string' ? pass.direction.toLowerCase() : null;
    const entry = directionalPassMap.get(name) || { forward: 0, lateral: 0, backward: 0 };
    if (dir === 'forward') entry.forward += 1;
    else if (dir === 'lateral') entry.lateral += 1;
    else if (dir === 'backward') entry.backward += 1;
    directionalPassMap.set(name, entry);
  });
  const bumpsMap = summarizeSimpleList(match.bumps);
  const touchesMap = summarizeSimpleList(match.touches);
  const saveMap = summarizeSaves(savesArr, shotSavedBy);
  const duelsMap = summarizeDuels(duelsArr);
  const reboundsMap = summarizeRebounds(reboundsArr);
  const reboundRecoveriesMap = summarizeClearanceRecoveries(reboundRecoveriesArr);
  const clearancesMap = summarizeClearances(clearancesArr);
  const clearanceRecoveriesMap = summarizeClearanceRecoveries(clearanceRecoveriesArr);
  const gkMap = summarizeGoalkeeperTicks(gkArr);
  const pressureMap = new Map();
  kicksArr.forEach((kick) => {
    if (!kick || typeof kick !== 'object') return;
    const name = resolvePlayerName(kick.player);
    if (!name) return;
    const entry = pressureMap.get(name) || { total: 0, pressured: 0, speedSum: 0 };
    entry.total += 1;
    if (kick.pressured) entry.pressured += 1;
    if (Number.isFinite(Number(kick.speed))) entry.speedSum += Math.abs(Number(kick.speed));
    pressureMap.set(name, entry);
  });

  const { possessions: possessionTimeline, advanced: advancedStats } = buildAdvancedPlayerStats(match);

  const legacyMetrics = new Map();
  legacyPlayers.forEach((entry) => {
    if (!entry) return;
    const nick = entry.nick || entry.player || entry.name || resolvePlayerName(entry);
    if (!nick) return;
    const metrics = entry.metrics && typeof entry.metrics === 'object' ? entry.metrics : entry;
    legacyMetrics.set(nick, metrics);
  });

  const positionAnalyticsLookup = buildPositionAnalyticsLookup(match, teamLookup);

  const playerSummaries = players.map((player) => {
    if (!player) return null;
    const nickRaw = player.nick || player.player || player.name || resolvePlayerName(player);
    const nick = typeof nickRaw === 'string' ? nickRaw.trim() : (nickRaw || '');
    if (!nick) return null;
    const normalizedNick = nick.trim();
    let team = 'spec';
    if (normalizedNick) {
      const lower = normalizedNick.toLowerCase();
      if (redLookup.has(lower)) team = 'red';
      else if (blueLookup.has(lower)) team = 'blue';
    }
    const legacy = legacyMetrics.get(nick) || {};
    if (team === 'spec') {
      const hintedTeam = (player.team || legacy.team || player.side || legacy.side || '').toString().toLowerCase();
      if (hintedTeam.startsWith('r')) team = 'red';
      else if (hintedTeam.startsWith('b')) team = 'blue';
    }
    if (team === 'spec' && inferredAssignments && normalizedNick) {
      const inferredKey = canonicalPlayerKey(normalizedNick);
      const inferred = inferredKey ? inferredAssignments.assignments?.get(inferredKey) : null;
      if (inferred && (inferred.team === 'red' || inferred.team === 'blue')) {
        team = inferred.team;
      }
    }
    const shotInfo = shotMap.get(nick) || { total: 0, onTarget: 0, offTarget: 0, xg: 0, xgOnTarget: 0 };
    const goalsCount = Math.max(getCount(goalMap, nick), toFiniteNumber(player.goals) || 0);
    const assistsCount = Math.max(getCount(assistMap, nick), toFiniteNumber(player.assists) || 0);
    const savesCount = Math.max(
      getCount(saveMap, nick),
      toFiniteNumber(player.saves) || 0,
      toFiniteNumber(player.goalkeeping?.saves) || 0,
      toFiniteNumber(legacy.saves) || 0
    );
    const advanced = advancedStats.get(nick) || {};
    const passesCompleted = Math.max(
      getCount(passMap, nick),
      extractCompletedPasses(player, 0),
      toFiniteNumber(legacy.passesCompleted) || 0,
      toFiniteNumber(advanced.passesCompleted) || 0
    );
    const attemptedPasses = extractAttemptedPasses(player, 0);
    const legacyAttempts = toFiniteNumber(legacy.passesAttempted) || 0;
    const passAttemptsCandidate = attemptedPasses > 0 ? attemptedPasses : legacyAttempts;
    const advancedTurnoverTouches = Math.max(
      Number(advanced.turnoverTouches) || 0,
      Number(advanced.turnoversCommitted) || 0
    );
    const advancedDangerousTouches = Math.max(Number(advanced.dangerousTurnovers) || 0, 0);
    let passAttempts = passAttemptsCandidate > 0 ? passAttemptsCandidate : passesCompleted;
    const baselineAttempts = passesCompleted + advancedTurnoverTouches + advancedDangerousTouches;
    if (baselineAttempts > passAttempts) passAttempts = baselineAttempts;
    const passAccuracy = passAttempts > 0 ? (passesCompleted / passAttempts) : 0;
    const duelsEntry = duelsMap.get(nick) || { total: 0, corner: 0, interception: 0, wins: 0, losses: 0 };
    const duelsWon = Number(duelsEntry.wins) || 0;
    const duelsLost = Number(duelsEntry.losses) || 0;
    const duelsCornerEvents = Number(duelsEntry.corner) || 0;
    const duelsInterceptionEvents = Number(duelsEntry.interception) || 0;
    const duelsTotalEvents = Number(duelsEntry.total) || 0;
    const duelsOpen = Math.max(safeNumber(player.openDuel), duelsTotalEvents - duelsCornerEvents - duelsInterceptionEvents);
    const duelsCorner = Math.max(safeNumber(player.cornerDuel), duelsCornerEvents);
    const duelsInterception = Math.max(safeNumber(player.duelsInterception ?? player.interceptionDuel), duelsInterceptionEvents);
    const interceptions = safeNumber(player.interceptions);
    const reboundsEntry = reboundsMap.get(nick) || { total: 0, clearanceRecoveries: 0 };
    const reboundTotals = Number(reboundsEntry.total) || 0;
    const reboundRecoveriesPrimary = Number(reboundsEntry.clearanceRecoveries) || 0;
    const reboundRecoveryEntry = reboundRecoveriesMap.get(nick) || { total: 0 };
    const reboundRecoveriesExtra = Number(reboundRecoveryEntry.total) || 0;
    const reboundRecoveries = reboundRecoveriesPrimary + reboundRecoveriesExtra;
    const clearancesEntry = clearancesMap.get(nick) || { total: 0 };
    const clearancesFromEvents = Number(clearancesEntry.total) || 0;
    const clearances = Math.max(
      safeNumber(player.clears),
      clearancesFromEvents,
      toFiniteNumber(legacy.clearances) || 0
    );
    const clearanceRecoveryEntry = clearanceRecoveriesMap.get(nick) || { total: 0 };
    const clearanceRecoveries = Number(clearanceRecoveryEntry.total) || 0;
    const bumps = Math.max(
      getCount(bumpsMap, nick),
      toFiniteNumber(player.bumps) || 0,
      toFiniteNumber(legacy.bumps) || 0
    );
    const touches = Math.max(
      getCount(touchesMap, nick),
      toFiniteNumber(player.touches) || 0,
      toFiniteNumber(player.totalTouches) || 0,
      toFiniteNumber(player.touchCount) || 0,
      toFiniteNumber(legacy.touches) || 0
    );
    const directional = directionalPassMap.get(nick) || { forward: 0, lateral: 0, backward: 0 };
    const gkEntry = gkMap.get(nick) || { ticks: 0 };
    const gkTicks = Number(gkEntry.ticks) || 0;
    const pressureEntry = pressureMap.get(nick) || { total: 0, pressured: 0, speedSum: 0 };
    const positionEntry = positionAnalyticsLookup.get(canonicalPlayerKey(normalizedNick));
    let positionSamples = 0;
    let avgPosX = null;
    let avgPosY = null;
    let positionHeatmap = null;
    let positionBounds = null;
    const positionFieldWidth = positionEntry ? positionEntry.fieldWidth : null;
    const positionFieldHeight = positionEntry ? positionEntry.fieldHeight : null;
    let positionSamplesByTeam = positionEntry && positionEntry.samplesByTeam
      ? {
          red: Number(positionEntry.samplesByTeam.red) || 0,
          blue: Number(positionEntry.samplesByTeam.blue) || 0,
          spec: Number(positionEntry.samplesByTeam.spec) || 0
        }
      : { red: 0, blue: 0, spec: 0 };
    if (positionEntry && positionEntry.samples > 0) {
      positionSamples = positionEntry.samples;
      avgPosX = positionEntry.sumX / positionEntry.samples;
      avgPosY = positionEntry.sumY / positionEntry.samples;
      positionHeatmap = positionEntry.heatmap ? cloneHeatmapGrid(positionEntry.heatmap) : null;
      positionBounds = {
        minX: positionEntry.minX,
        maxX: positionEntry.maxX,
        minY: positionEntry.minY,
        maxY: positionEntry.maxY
      };
    }
    const spatialMeta = computePlayerSpatial({
      team,
      metrics: {
        positionHeatmap,
        positionSamples,
        positionFieldWidth,
        positionFieldHeight,
        avgPosX,
        avgPosY,
        positionMinX: positionBounds ? positionBounds.minX : null,
        positionMaxX: positionBounds ? positionBounds.maxX : null,
        positionMinY: positionBounds ? positionBounds.minY : null,
        positionMaxY: positionBounds ? positionBounds.maxY : null,
        touches
      }
    }, null);
    const kicksTotal = Number(pressureEntry.total) || 0;
    const pressuredKicks = Number(pressureEntry.pressured) || 0;
    const avgKickSpeed = kicksTotal > 0 ? pressureEntry.speedSum / kicksTotal : 0;
    const directXg = extractPlayerXg(player, 0);
    const legacyXg = toFiniteNumber(legacy.xg) || 0;
    const aggregatedXg = legacyXg > 0 ? legacyXg : (directXg > 0 ? directXg : shotInfo.xg);
    const passesPerMinute = passesCompleted / minutes;
    const touchesPerMinute = touches / minutes;
    const savesPerMinute = savesCount / minutes;
    const shotsOnTarget = Math.max(
      shotInfo.onTarget || 0,
      toFiniteNumber(player.shotsOnTarget) || 0,
      toFiniteNumber(legacy.shotsOnTarget) || 0
    );
    const shotsOffTarget = Math.max(
      Number.isFinite(shotInfo.offTarget) ? shotInfo.offTarget : ((shotInfo.total || 0) - (shotInfo.onTarget || 0)),
      toFiniteNumber(player.shotsOffTarget) || 0,
      toFiniteNumber(player.shotsWide) || 0,
      toFiniteNumber(legacy.shotsOffTarget) || 0
    );
    const shotsTotal = Math.max(
      shotInfo.total || (shotsOnTarget + shotsOffTarget),
      toFiniteNumber(player.shots) || 0,
      toFiniteNumber(legacy.shotsTotal) || (shotsOnTarget + shotsOffTarget)
    );

    const defensiveInterventions = interceptions + clearances + reboundRecoveries + clearanceRecoveries + duelsWon;
    const keeperShare = gkTicks / totalTicks;
    const depthImpactScore = clamp((defensiveInterventions / minutes) * 0.08 + keeperShare * 0.6, 0, 2.5);

    const duelsTotal = Math.max(duelsTotalEvents, duelsOpen + duelsCorner + duelsInterception);

    return {
      nick,
      team,
      player,
      metrics: {
        goals: goalsCount,
        assists: assistsCount,
        saves: savesCount,
        shotsTotal,
        shotsOnTarget,
        shotsOffTarget,
        xg: aggregatedXg,
        xgOnTarget: shotInfo.xgOnTarget,
        passesCompleted,
        passesAttempted: passAttempts,
        passAccuracy,
        passesPerMinute,
        duelsOpen,
        duelsCorner,
        duelsInterception,
        duelsTotal,
        duelsWon,
        duelsLost,
        interceptions,
        clearances,
        bumps,
        touches,
        touchesPerMinute,
        savesPerMinute,
        rebounds: Math.max(reboundTotals, toFiniteNumber(legacy.rebounds) || 0),
        reboundRecoveries,
        clearanceEvents: clearancesFromEvents,
        clearanceRecoveries,
        passesForward: directional.forward,
        passesLateral: directional.lateral,
        passesBackward: directional.backward,
        gkTicks,
        kicksTotal,
        pressuredKicks,
        avgKickSpeed,
        pressuredRate: kicksTotal > 0 ? pressuredKicks / kicksTotal : 0,
        depthImpactScore,
        possessionTouches: Number(advanced.possessionTouches) || 0,
        possessionStarts: Number(advanced.possessionStarts) || 0,
        scoringPossessionTouches: Number(advanced.scoringPossessionTouches) || 0,
        turnoverTouches: Number(advanced.turnoverTouches) || 0,
        pressuredPossessionTouches: Number(advanced.pressuredPossessionTouches) || 0,
        dangerousTurnovers: Number(advanced.dangerousTurnovers) || 0,
        turnoversCommitted: Number(advanced.turnoversCommitted) || 0,
        forcedTurnovers: Number(advanced.forcedTurnovers) || 0,
        progressivePasses: Number(advanced.progressivePasses) || 0,
        progressiveDistance: Number(advanced.progressiveDistance) || 0,
        finalThirdEntries: Number(advanced.finalThirdEntries) || 0,
        keyPasses: Number(advanced.keyPasses) || 0,
        recoveries: Number(advanced.recoveries) || 0,
        finalThirdRecoveries: Number(advanced.finalThirdRecoveries) || 0,
        positionSamples,
        avgPosX,
        avgPosY,
        positionHeatmap,
        positionBounds,
        positionSamplesByTeam,
        positionMinX: positionBounds ? positionBounds.minX : null,
        positionMaxX: positionBounds ? positionBounds.maxX : null,
        positionMinY: positionBounds ? positionBounds.minY : null,
        positionMaxY: positionBounds ? positionBounds.maxY : null,
        positionFieldWidth,
        positionFieldHeight,
        positionSamplesRed: positionSamplesByTeam?.red || 0,
        positionSamplesBlue: positionSamplesByTeam?.blue || 0,
        positionSamplesSpec: positionSamplesByTeam?.spec || 0,
        positionDepth: spatialMeta?.depth ?? 0,
        positionLateral: spatialMeta?.lateral ?? 0,
        positionWideShare: spatialMeta?.wideShare ?? 0,
        positionCornerShare: spatialMeta?.cornerShare ?? 0,
        positionTopShare: spatialMeta?.topShare ?? 0,
        positionBottomShare: spatialMeta?.bottomShare ?? 0,
        positionTopCornerShare: spatialMeta?.topCornerShare ?? 0,
        positionBottomCornerShare: spatialMeta?.bottomCornerShare ?? 0,
        positionAttShare: spatialMeta?.attShare ?? 0,
        positionMidShare: spatialMeta?.midShare ?? 0,
        positionDefShare: spatialMeta?.defShare ?? 0,
        positionAttMinusDef: spatialMeta?.attMinusDef ?? 0,
        positionEntropy: spatialMeta?.entropy ?? 0,
        positionCentrality: spatialMeta?.centrality ?? 0,
        positionVerticalBalance: spatialMeta?.verticalBalance ?? 0,
        xgFaced: Number(advanced.xgFaced) || 0,
        goalsConceded: Number(advanced.goalsConceded) || 0,
        xgPrevented: Number(advanced.xgPrevented) || 0,
        sweeperActions: Number(advanced.sweeperActions) || 0,
        goalChainTouches: Number(advanced.goalChainTouches) || 0,
        secondaryAssistsChain: Number(advanced.secondaryAssists) || 0,
        tertiaryAssistsChain: Number(advanced.tertiaryAssists) || 0,
        counterRuns: Number(advanced.counterRuns) || 0,
        lastDefenderEvents: Number(advanced.lastDefenderEvents) || 0,
        xgShare: 0,
        saveShare: 0,
        resultFactor: 1
      },
      ruleRating: 0,
      teamGoalsFor: 0,
      teamGoalsAgainst: 0,
      teamMargin: 0,
      role: 'midfielder'
    };
  }).filter(Boolean);

  const { derivedByNick, statsByTeam } = prepareTeamDerivedMetricsInternal(playerSummaries, minutes, totalTicks);

  playerSummaries.forEach((summary) => {
    if (!summary || (summary.team !== 'red' && summary.team !== 'blue')) return;
    const derived = derivedByNick.get(summary.nick) || {};
    summary.role = determineRole(summary, derived);
  });

  applyPositionTags(playerSummaries, derivedByNick);

  const roleBaselines = computeRoleBaselines(playerSummaries, derivedByNick);

  const teamXgTotals = { red: 0, blue: 0 };
  playerSummaries.forEach((summary) => {
    if (summary.team === 'red') teamXgTotals.red += summary.metrics.xg;
    else if (summary.team === 'blue') teamXgTotals.blue += summary.metrics.xg;
  });

  const totalXg = teamXgTotals.red + teamXgTotals.blue;
  const xgShare = {
    red: totalXg > 0 ? clamp(teamXgTotals.red / totalXg, 0, 1) : 0.5,
    blue: totalXg > 0 ? clamp(teamXgTotals.blue / totalXg, 0, 1) : 0.5
  };

  const dominanceScore = {
    red: clamp(xgShare.red * 0.5 + possessionShare.red * 0.3 + territoryShare.red * 0.2, 0, 1.2),
    blue: clamp(xgShare.blue * 0.5 + possessionShare.blue * 0.3 + territoryShare.blue * 0.2, 0, 1.2)
  };

  const reportedRedSaves = safeNumber(match.savesRed);
  const reportedBlueSaves = safeNumber(match.savesBlue);
  const manualRedSaves = playerSummaries.reduce((sum, summary) => summary.team === 'red' ? sum + summary.metrics.saves : sum, 0);
  const manualBlueSaves = playerSummaries.reduce((sum, summary) => summary.team === 'blue' ? sum + summary.metrics.saves : sum, 0);
  const teamSaveTotals = {
    red: reportedRedSaves > 0 ? reportedRedSaves : manualRedSaves,
    blue: reportedBlueSaves > 0 ? reportedBlueSaves : manualBlueSaves
  };

  const scoreRed = safeNumber(match.scoreRed);
  const scoreBlue = safeNumber(match.scoreBlue);
  const marginGlobal = scoreRed - scoreBlue;

  const goalsSignature = goals.map((g) => {
    const scorer = g && g.scorer ? g.scorer : '?';
    const scoreArr = Array.isArray(g && g.currentScore) ? g.currentScore.join('-') : '';
    const goalIndex = g && g.goalIndex !== undefined ? g.goalIndex : '';
    return `${scorer}:${scoreArr}:${goalIndex}`;
  }).join('|');

  const playerSignature = playerSummaries.map((summary) => {
    return `${summary.nick}:${roundTo(summary.metrics.xg, 3)}:${summary.metrics.goals}:${summary.metrics.assists}:${summary.metrics.passesCompleted}`;
  }).join('|');

  const signatureBase = [
    `${scoreRed}:${scoreBlue}:${safeNumber(match.gameTicks)}`,
    redTeam.join(','),
    blueTeam.join(','),
    playerSignature,
    goalsSignature
  ].join('#');

  const signature = fnv1a(signatureBase);

  return {
    match,
    minutes,
    playerSummaries,
    teamXgTotals,
    teamSaveTotals,
    possessionShare,
    territoryShare,
    teamDominance: dominanceScore,
    margin: marginGlobal,
    signature,
    derivedByNick,
    teamMetricStats: statsByTeam,
    possessionTimeline,
    roleBaselines
  };
}

export function applyRuleRatings(baseAnalysis) {
  if (!baseAnalysis || !baseAnalysis.match) return baseAnalysis;
  const minutes = baseAnalysis.minutes || 1;
  const scale = 10 / Math.max(1, minutes);
  const scoreRed = safeNumber(baseAnalysis.match.scoreRed);
  const scoreBlue = safeNumber(baseAnalysis.match.scoreBlue);
  const derivedByNick = baseAnalysis.derivedByNick || new Map();
  const teamStats = baseAnalysis.teamMetricStats || { red: {}, blue: {} };
  const dominance = baseAnalysis.teamDominance || { red: 0.5, blue: 0.5 };

  const updatedSummaries = baseAnalysis.playerSummaries.map((summary) => {
    if (!summary || (summary.team !== 'red' && summary.team !== 'blue')) {
      return {
        ...summary,
        ruleRating: 0,
        metrics: {
          ...summary.metrics,
          resultFactor: 1
        }
      };
    }

    const derived = derivedByNick instanceof Map
      ? (derivedByNick.get(summary.nick) || {})
      : (derivedByNick?.[summary.nick] || {});
    const weights = ROLE_WEIGHTS[summary.role] || ROLE_WEIGHTS.midfielder;

    const passAccuracy = clamp(derived.passAccuracy || 0, 0, 1);
    const passesPerMinute = derived.passesPerMinute || 0;
    const touchesPerMinute = derived.touchesPerMinute || 0;
    const pressureRelief = clamp(derived.pressureRelief || 0, 0, 1);
    const pressureRate = clamp(derived.pressureRate || 0, 0, 1);
    const duelDiff = (summary.metrics.duelsWon || 0) - (summary.metrics.duelsLost || 0);
    const isRed = summary.team === 'red';
    const teamGoalsFor = isRed ? scoreRed : scoreBlue;
    const teamGoalsAgainst = isRed ? scoreBlue : scoreRed;
    const margin = isRed ? baseAnalysis.margin : -baseAnalysis.margin;
    const teamDom = isRed ? dominance.red : dominance.blue;
    const oppDom = isRed ? dominance.blue : dominance.red;
    const dominanceEdge = clamp(teamDom - oppDom, -1, 1);

    const offenseContribution = clamp(
      (summary.metrics.xg || 0) * 0.6 +
        (summary.metrics.goals * scale) * 0.5 +
        (summary.metrics.assists * scale) * 0.3 +
        clamp((summary.metrics.goals || 0) - (summary.metrics.xg || 0), -1.2, 1.2) * 0.4 +
        boundZ(
          zScore(derived.xg, teamStats[summary.team]?.xg) * 0.7 +
          zScore(derived.goals, teamStats[summary.team]?.goals) * 0.6 +
          zScore(derived.assists, teamStats[summary.team]?.assists) * 0.45 +
          zScore(derived.shotsOnTarget, teamStats[summary.team]?.shotsOnTarget) * 0.3,
          2.5
        ),
      -1.1,
      3
    );

    const defenseContribution = clamp(
      (summary.metrics.saves * scale) * 0.5 +
        (derived.defensiveImpact || 0) * 0.9 +
        (derived.recoveryPerMinute || 0) * 0.7 +
        (derived.clearancePerMinute || 0) * 0.6 +
        (derived.depthImpactScore || 0) * 0.55 +
        boundZ(
          zScore(derived.defensiveImpact, teamStats[summary.team]?.defensiveImpact) * 0.65 +
          zScore(derived.savesPerMinute, teamStats[summary.team]?.savesPerMinute) * 0.55 +
          zScore(derived.keeperShare, teamStats[summary.team]?.keeperShare) * 0.4,
          2.5
        ),
      -1,
      2.6
    );

    const playmakingContribution = clamp(
      Math.max(0, passesPerMinute - 1.3) * 0.6 +
        Math.max(0, touchesPerMinute - 3) * 0.2 +
        Math.max(0, passAccuracy - 0.78) * 1.3 +
        boundZ(
          zScore(passesPerMinute, teamStats[summary.team]?.passesPerMinute) * 0.6 +
          zScore(passAccuracy, teamStats[summary.team]?.passAccuracy) * 0.5 +
          zScore(touchesPerMinute, teamStats[summary.team]?.touchesPerMinute) * 0.35 +
          zScore(derived.progressiveBalance, teamStats[summary.team]?.progressiveBalance) * 0.3,
          2.5
        ),
      -0.9,
      2.4
    );

    const pressureContribution = clamp(
      boundZ(
        zScore(pressureRelief, teamStats[summary.team]?.pressureRelief) * 0.5 -
        zScore(pressureRate, teamStats[summary.team]?.pressureRate) * 0.6,
        2
      ) +
        Math.max(0, pressureRelief - 0.55) * 0.8 -
        Math.max(0, pressureRate - 0.65) * 0.9,
      -1,
      1
    );

    const duelContribution = clamp(
      boundZ(zScore(derived.duelWinRate, teamStats[summary.team]?.duelWinRate) * 0.6, 2.5) +
        duelDiff * 0.15 +
        (summary.metrics.duelsInterception || 0) * 0.12 +
        (summary.metrics.duelsCorner || 0) * 0.08,
      -0.8,
      1.6
    );

    const defensiveActivity = (derived.defensiveImpact || 0) + (derived.clearancePerMinute || 0) + (derived.recoveryPerMinute || 0);
    const activityLoad = passesPerMinute + touchesPerMinute + defensiveActivity + ((summary.metrics.saves * scale) / Math.max(scale, 0.1));
    const activityAdj = clamp((activityLoad - 4) * 0.3, -1.2, 1);

    let mistakePenalty = 0;
    if (passesPerMinute >= 1.4 && passAccuracy < 0.7) mistakePenalty += Math.min(0.7 - passAccuracy, 0.35) * 1.1;
    if (pressureRate > 0.72) mistakePenalty += Math.min(pressureRate - 0.72, 0.4) * 1;
    if ((summary.metrics.gkTicks || 0) > 0 && summary.metrics.saves < 1) mistakePenalty += 0.25;

    const teamXg = isRed ? baseAnalysis.teamXgTotals.red : baseAnalysis.teamXgTotals.blue;
    const teamXgShare = teamXg > 0 ? clamp(summary.metrics.xg / teamXg, 0, 1.5) : 0;
    const teamSaves = isRed ? baseAnalysis.teamSaveTotals.red : baseAnalysis.teamSaveTotals.blue;
    const saveShare = teamSaves > 0 ? clamp(summary.metrics.saves / teamSaves, 0, 1.5) : 0;

    const resultContribution = clamp(margin * 0.23, -1.2, 1.2) + clamp(dominanceEdge * 0.48, -0.75, 0.75);
    const shareContribution = teamXgShare * 0.32 + saveShare * 0.28;
    const clutchBonus = summary.metrics.goals >= 1 && margin <= 0 ? 0.3 : 0;

    const rawImpact =
      (summary.metrics.goals || 0) * 0.32 +
      (summary.metrics.assists || 0) * 0.22 +
      (summary.metrics.xg || 0) * 0.2 +
      (summary.metrics.saves || 0) * 0.15 +
      (summary.metrics.interceptions || 0) * 0.07 +
      (summary.metrics.clearances || 0) * 0.06 +
      (summary.metrics.reboundRecoveries || 0) * 0.07 +
      (summary.metrics.clearanceRecoveries || 0) * 0.07 +
      (summary.metrics.duelsWon || 0) * 0.05;

    const finalRaw =
      5.3 +
      offenseContribution * weights.offense +
      defenseContribution * weights.defense +
      playmakingContribution * weights.playmaking +
      pressureContribution * weights.pressure +
      duelContribution * weights.duel +
      activityAdj +
      resultContribution +
      shareContribution +
      clutchBonus +
      clamp(rawImpact * 0.08, -1, 1.6) -
      mistakePenalty;

    const ruleScore = clamp(finalRaw, 2, 9.4);
    const advancedScore = computeAdvancedScore(summary, derived, baseAnalysis, minutes, scale);
    const finalScore = clamp(ruleScore * 0.45 + advancedScore * 0.55, 2, 9.5);
  const resultFactor = clamp(1 + margin * 0.04 + dominanceEdge * 0.04, 0.75, 1.2);

    return {
      ...summary,
      teamGoalsFor,
      teamGoalsAgainst,
      teamMargin: margin,
      ruleRating: Number(finalScore.toFixed(2)),
      metrics: {
        ...summary.metrics,
        xgShare: summary.metrics.xgShare ?? teamXgShare,
        saveShare,
        resultFactor,
        dominanceEdge,
        possessionShare: summary.team === 'red' ? baseAnalysis.possessionShare.red : baseAnalysis.possessionShare.blue,
        territoryShare: summary.team === 'red' ? baseAnalysis.territoryShare.red : baseAnalysis.territoryShare.blue,
        ruleBaseline: Number(ruleScore.toFixed(3)),
        advancedScore: Number(advancedScore.toFixed(3))
      }
    };
  });

  return {
    ...baseAnalysis,
    playerSummaries: updatedSummaries
  };
}

export function fnv1a(str) {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
}

export const __TEST_ONLY__ = {
  scoreStrikerCandidate,
  chooseStrikerCandidate,
  resolveWingPosition,
  scoreCenterBackCandidate,
  chooseCenterBacks,
  buildPositionAnalyticsLookup,
  buildFallbackPositionAnalytics,
  buildFflFeatureRows
};
